<template>
	<div id="oneroster-config" data-cy="oneroster-config">
		<v-card class="mb-4">
			<v-card-title class="d-flex align-center justify-space-between">
				<span v-if="configData.version !== 'nycdoe'">OneRoster Configuration</span>
				<span v-else>NYC DOE OneRoster Configuration</span>
				<v-btn
					v-if="!showConfigForm"
					icon
					@click="showConfigForm = true"
					size="small"
					rounded="lg"
					color="grey lighten-1"
					title="Edit Configuration"
				>
					<v-icon size="18">mdi-pencil</v-icon>
				</v-btn>
			</v-card-title>
			<v-card-text v-if="showConfigForm">
				<div>
					<v-form ref="configForm">
						<v-row>
							<v-col cols="12" md="6">
								<v-text-field
									v-model="configData.client_id"
									label="Client ID"
									variant="outlined"
									density="comfortable"
									required
									v-if="props.integration_type !== 'nycdoe'"
								></v-text-field>
								<input type="hidden" v-model="configData.client_id" v-else />
							</v-col>
							<v-col cols="12" md="6">
								<v-text-field
									v-model="configData.client_secret"
									label="Client Secret"
									variant="outlined"
									density="comfortable"
									required
									v-if="props.integration_type !== 'nycdoe'"
								></v-text-field>
								<input type="hidden" v-model="configData.client_secret" v-else />
							</v-col>
							<v-col cols="12" md="6">
								<v-text-field
									v-model="configData.token_url"
									label="Token URL"
									variant="outlined"
									density="comfortable"
									required
									v-if="props.integration_type !== 'nycdoe'"
								></v-text-field>
								<input type="hidden" v-model="configData.token_url" v-else />
							</v-col>
							<v-col cols="12" md="6">
								<v-text-field
									v-model="configData.base_url"
									label="Base URL"
									variant="outlined"
									density="comfortable"
									required
									v-if="props.integration_type !== 'nycdoe'"
								></v-text-field>
								<input type="hidden" v-model="configData.base_url" v-else />
							</v-col>
							<v-col cols="12" md="6">
								<v-text-field
									v-model="configData.url_suffix"
									label="URL Suffix"
									variant="outlined"
									density="comfortable"
									v-if="props.integration_type !== 'nycdoe'"
								></v-text-field>
								<v-text-field
									v-model="configData.url_suffix"
									label="DBN Code (School Code)"
									variant="outlined"
									density="comfortable"
									v-else
								></v-text-field>
							</v-col>
							<v-col cols="12" md="6">
								<v-text-field
									v-model="configData.version"
									label="API Version"
									variant="outlined"
									density="comfortable"
									required
									v-if="props.integration_type !== 'nycdoe'"
								></v-text-field>
								<input type="hidden" v-model="configData.version" v-else />
							</v-col>
						</v-row>
					</v-form>
				</div>
			</v-card-text>
			<v-card-actions v-if="showConfigForm">
				<v-spacer />
				<v-btn color="grey lighten-1" @click="cancelConfigEdit" :disabled="savingConfig">
					Cancel
				</v-btn>
				<v-btn color="primary" @click="saveConfig" :loading="savingConfig">
					Save Configuration
				</v-btn>
			</v-card-actions>
		</v-card>

		<!-- Data Sync Section -->
		<v-card class="mb-4">
			<v-card-title class="d-flex align-center justify-space-between">
				<div class="d-flex align-center">
					<span>OneRoster Data Sync</span>
				</div>
				<!-- School Mapping Button -->
				<v-tooltip bottom>
					<template v-slot:activator="{ props }">
						<v-btn
							icon
							color="primary"
							size="small"
							rounded="lg"
							@click="showSchoolMapping = true"
							class="mapping-btn"
							v-bind="props"
						>
							<v-icon>mdi-school</v-icon>
						</v-btn>
					</template>
					<span>Show school associations</span>
				</v-tooltip>
			</v-card-title>
			<v-card-text>
				<v-row>
					<v-col cols="12">
						<v-select
							v-model="selectedEntities"
							:items="getEntityOptions"
							item-title="title"
							item-value="value"
							label="Select Entities to Sync"
							multiple
							chips
							closable-chips
							variant="outlined"
							density="comfortable"
						></v-select>
					</v-col>
				</v-row>
			</v-card-text>

			<v-card-actions>
				<!-- Sync Logs/Status Button -->
				<v-tooltip bottom>
					<template v-slot:activator="{ props }">
						<v-btn
							icon
							color="info"
							size="small"
							rounded="lg"
							@click="showSyncLogs = true"
							class="logs-btn ml-2"
							v-bind="props"
						>
							<v-icon>mdi-text-box-search-outline</v-icon>
						</v-btn>
					</template>
					<span>See sync status</span>
				</v-tooltip>
				<v-spacer />
				<!-- <v-btn
					color="error"
					:disabled="!syncing"
					variant="outlined"
					@click="cancelSync"
					v-if="syncing"
					class="mr-2"
				>
					Cancel Sync
				</v-btn> -->
				<v-btn
					color="primary"
					:disabled="!hasValidConfig || !selectedEntities.length || syncing"
					:loading="syncing"
					variant="elevated"
					@click="syncData"
				>
					Sync Selected Data
				</v-btn>
				<!-- Sync Progress Bar -->
			</v-card-actions>
			<v-progress-linear
				indeterminate
				color="secondary"
				v-if="syncing"
			></v-progress-linear>
		</v-card>
	</div>

	<!-- Sync Logs/Status Dialog -->
	<v-dialog v-model="showSyncLogs" max-width="800px">
		<v-card>
			<v-card-title class="d-flex align-center justify-space-between">
				<span>Sync Status & Logs</span>
				<v-tooltip bottom>
					<template v-slot:activator="{ props }">
						<v-btn
							icon
							:loading="loadingSyncLogs"
							:disabled="!!syncInterval"
							@click="fetchSyncStatus"
							size="small"
							rounded="lg"
							color="grey lighten-1"
							v-bind="props"
						>
							<v-icon size="18">mdi-refresh</v-icon>
						</v-btn>
					</template>
					<span>Refresh logs</span>
				</v-tooltip>
			</v-card-title>
			<v-card-text>
				<!-- Current Sync Status -->

				<!-- Sync Logs -->
				<div class="sync-logs-section">
					<h4 class="mb-2">Recent Sync Details</h4>
					<div class="logs-container">
						<div
							v-if="syncLogs.length === 0"
							class="text-body-2 text-medium-emphasis pa-4 text-center"
						>
							No sync records available
						</div>
						<div v-else>
							<div
								v-for="(syncRecord, index) in syncLogs"
								:key="index"
								class="sync-record mb-4"
							>
								<!-- Sync Record Header -->
								<div class="sync-header p-3 mb-2" :class="getSyncRecordClass(syncRecord)">
									<div class="d-flex align-center justify-space-between">
										<div class="d-flex align-center">
											<v-icon :color="getLogIconColor(syncRecord)" size="20" class="mr-2">
												{{ getLogIcon(syncRecord) }}
											</v-icon>
											<div>
												<strong>{{ syncRecord.status }}</strong>
												<span v-if="syncRecord.current_entity" class="ml-2 text-body-2">
													{{ syncRecord.completed_entities }}/{{
														syncRecord.total_entities
													}}
													entities
												</span>
											</div>
										</div>
										<div class="text-body-2">
											<div v-if="syncRecord.total_entities">
												<span
													v-if="syncRecord.failed_entities > 0"
													class="text-error ml-1"
												>
													({{ syncRecord.failed_entities }} failed)
												</span>
											</div>
											<div class="mt-1">
												<div>Started: {{ formatLogTime(syncRecord.started_at) }}</div>
												<div v-if="syncRecord.completed_at">
													Completed: {{ formatLogTime(syncRecord.completed_at) }}
												</div>
											</div>
										</div>
									</div>
									<div
										v-if="syncRecord.error_message"
										class="error-message pa-2 mb-2 text-error"
									>
										<v-icon size="16" class="mr-1">mdi-alert-circle</v-icon>
										{{ syncRecord.error_message }}
									</div>
								</div>

								<!-- Entity Details -->
								<div
									v-if="syncRecord.entities && syncRecord.entities.length > 0"
									class="entity-details"
								>
									<h5 class="mb-2">Entity Details</h5>
									<div class="entity-grid">
										<div
											v-for="entity in syncRecord.entities"
											:key="entity.entity"
											class="entity-card pa-2 mb-2"
											:class="getEntityClass(entity)"
										>
											<div class="d-flex align-center justify-space-between">
												<div class="d-flex align-center">
													<v-icon :color="getLogIconColor(entity)" size="16" class="mr-2">
														{{ getLogIcon(entity) }}
													</v-icon>
													<strong>{{ entity.entity }}</strong>
												</div>
												<span class="entity-status">{{ entity.status }}</span>
											</div>
											<div class="entity-stats mt-1 text-body-2">
												<div
													style="
														display: flex;
														justify-content: space-between;
														align-items: center;
													"
												>
													<div>Fetched: {{ entity.records_fetched || 0 }} records</div>
													<div>Merged: {{ entity.records_merged || 0 }} records</div>
												</div>
												<div v-if="entity.error_message" class="text-error mt-1">
													Error: {{ entity.error_message }}
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</v-card-text>
			<v-card-actions>
				<v-spacer />
				<v-btn color="grey lighten-1" @click="showSyncLogs = false">Close</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>

	<!-- School Mapping Dialog -->
	<v-dialog v-model="showSchoolMapping" max-width="900px">
		<v-card>
			<v-card-title class="d-flex align-center justify-space-between">
				<span>Schools Mapping</span>
				<v-tooltip bottom>
					<template v-slot:activator="{ props }">
						<v-btn
							icon
							:loading="loading"
							@click="fetchOneRosterSchools"
							class="reload-btn"
							title="Reload"
							size="small"
							rounded="lg"
							color="grey lighten-1"
							v-bind="props"
						>
							<v-icon size="18">mdi-refresh</v-icon>
						</v-btn>
					</template>
					<span>Reload the list of schools</span>
				</v-tooltip>
			</v-card-title>
			<v-divider></v-divider>
			<v-card-text>
				<div class="oneroster-table-container">
					<table class="oneroster-table">
						<thead>
							<tr>
								<th class="header-cell">OneRoster School Name</th>
								<th class="header-cell">PTC Wizard School Name</th>
								<th class="header-cell actions-header">Create school</th>
							</tr>
						</thead>
					</table>
					<div class="table-body-scroll">
						<table class="oneroster-table">
							<tbody>
								<tr v-for="row in oneroster_schools" :key="row.sourced_id">
									<td>{{ row.name }}</td>
									<td style="min-width: 250px">
										<v-autocomplete
											v-model="row.school_id"
											:items="schools"
											item-title="school_name"
											item-value="school_id"
											density="comfortable"
											clearable
											placeholder="Select school..."
										/>
									</td>
									<td class="actions-header">
										<v-btn
											color="primary"
											size="small"
											icon="mdi-database-plus"
											@click="createSchoolFromOneRoster(row)"
											:loading="row.creating"
											v-if="!row.school_id"
										>
										</v-btn>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
			</v-card-text>
			<v-card-actions>
				<v-spacer />
				<v-btn color="grey lighten-1" @click="showSchoolMapping = false">Cancel</v-btn>
				<v-btn color="success" :loading="saving" @click="saveAndClose">Save</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from "vue";
import { useDistrictStore } from "@/stores/district";
import { useSchoolStore } from "@/stores/school";
import FetchService from "@/services/fetch.service";

// Define props
interface Props {
	integration_type?: string;
}

const props = withDefaults(defineProps<Props>(), {
	integration_type: "1.1",
});

// Watch for changes in integration_type prop
import { watch } from "vue";

watch(
	() => props.integration_type,
	(newVal) => {
		// Update configData.version
		if (newVal === "nycdoe") {
			configData.value.version = "nycdoe";
		} else {
			// For non-nycdoe integrations, preserve 1.2 if already set, otherwise default to 1.1
			configData.value.version = configData.value.version === "1.2" ? "1.2" : "1.1";
		}
		// Optionally, update selectedEntities to match new entity options
		selectedEntities.value = getEntityOptions.value.map((option) => option.value);
	}
);

interface OneRosterSchool {
	school_id: number | null;
	name: string;
	sourced_id: string;
	creating?: boolean;
}

interface OneRosterConfig {
	school_id?: number;
	district_id?: number;
	client_id: string;
	client_secret: string;
	token_url: string;
	base_url: string;
	url_suffix: string | null;
	version: string;
}

interface SyncStatus {
	message: string;
	color: string;
}

const districtStore = useDistrictStore();
const schoolStore = useSchoolStore();
const configForm = ref(null);
const schools = ref<{ school_id: number; school_name: string }[]>([]);
const config = ref<OneRosterConfig | null>(null);
const configData = ref<OneRosterConfig>({
	client_id: "",
	client_secret: "",
	token_url: "",
	base_url: "",
	url_suffix: null,
	version: "1.1", // Will be overridden in onMounted based on integration_type
});
const showConfigForm = ref(false);
const savingConfig = ref(false);
const showSchoolMapping = ref(false);

// Sync section

const getEntityOptions = computed(() => {
	if (props.integration_type == "nycdoe") {
		return [
			{ title: "Students", value: "students" },
			{ title: "Courses", value: "courses" },
			{ title: "Classes", value: "classes" },
			{ title: "Teachers", value: "teachers" },
			{ title: "Student Enrollment", value: "student_enrollment" },
			{ title: "Teacher Enrollment", value: "teacher_enrollment" },
			{ title: "Schools", value: "schools" },
		];
	} else {
		return [
			{ title: "Students", value: "students" },
			{ title: "Courses", value: "courses" },
			{ title: "Classes", value: "classes" },
			{ title: "Teachers", value: "teachers" },
			{ title: "Parents", value: "parents" },
			{ title: "Student Enrollment", value: "student_enrollment" },
			{ title: "Teacher Enrollment", value: "teacher_enrollment" },
			{ title: "Schools", value: "schools" },
		];
	}
});
const selectedEntities = ref<string[]>([]);
const syncing = ref(false);
const syncStatus = ref<SyncStatus | null>(null);

// Sync logs and status
const showSyncLogs = ref(false);
const loadingSyncLogs = ref(false);
const currentSyncStatus = ref<any>(null);
const syncLogs = ref<any[]>([]);
const oneroster_schools = ref<OneRosterSchool[]>([]);
const saving = ref(false);
const loading = ref(false);
const syncInterval = ref<NodeJS.Timeout | null>(null);

// Constants
const SYNC_STATUS_COMPLETED = "COMPLETED";

const hasValidConfig = computed(() => {
	return (
		!!config.value &&
		!!config.value.client_id &&
		!!config.value.client_secret &&
		!!config.value.token_url &&
		!!config.value.base_url
	);
});

const fetchSchools = async () => {
	await districtStore.fetchSchools();
	if (config.value?.version == "nycdoe") {
		console.log("Fetching schools for NYC DOE version");
		if (!schoolStore.school) {
			console.warn("No school data available in store");
			return [];
		} else {
			schools.value = [
				{
					school_id: schoolStore.school.school_id,
					school_name: schoolStore.school.school_name,
				},
			];
		}
	} else {
		if (!Array.isArray(districtStore.schools)) {
			return [];
		}
		schools.value = districtStore.schools
			.filter((s) => s && s.school_id != null && s.school_name != null)
			.map((s) => ({
				school_id: Number(s.school_id),
				school_name: String(s.school_name),
			}));
	}
};

function isSyncCompleted() {
	// Check if any sync record has COMPLETED status
	if (!syncLogs.value || syncLogs.value.length === 0) {
		// If no logs available, sync is not completed
		return true;
	}
	// Look for any entry with COMPLETED status
	const completedEntry = syncLogs.value.find(
		(entry) => entry.status?.toLowerCase() === SYNC_STATUS_COMPLETED.toLowerCase()
	);
	// Return true if we found any completed entry
	return !!completedEntry;
}
const fetchOneRosterConfig = async () => {
	try {
		const res = await FetchService.fetchData("/integration/oneroster", "get");
		if (res) {
			config.value = res;
			// Initialize the form data with the current config
			configData.value = { ...res };
			// Override version with prop value only for nycdoe, preserve 1.2 for others
			if (props.integration_type === "nycdoe") {
				configData.value.version = "nycdoe";
			} else {
				// For non-nycdoe integrations, preserve 1.2 if already saved, otherwise default to 1.1
				configData.value.version = configData.value.version === "1.2" ? "1.2" : "1.1";
			}
			// If config is null, show the form by default
			showConfigForm.value = !res;
		}
	} catch (error) {
		console.error("Error fetching OneRoster config:", error);
		showConfigForm.value = true;
	}
};

const saveConfig = async () => {
	savingConfig.value = true;
	if (props.integration_type === "nycdoe") {
		configData.value.version = "nycdoe";
	} else {
		// For non-nycdoe integrations, allow user to specify 1.2, otherwise default to 1.1
		configData.value.version = configData.value.version === "1.2" ? "1.2" : "1.1";
	}
	try {
		const res = await FetchService.fetchData(
			"/integration/oneroster",
			"put",
			configData.value
		);
		if (res) {
			config.value = res;
			configData.value = { ...res };
			showConfigForm.value = false;
		}
	} catch (error) {
		console.error("Error saving OneRoster config:", error);
	} finally {
		savingConfig.value = false;
	}
};

const cancelConfigEdit = () => {
	// Reset form data and hide form
	if (config.value) {
		configData.value = { ...config.value };
	}
	showConfigForm.value = false;
};

const maskSecret = (secret: string): string => {
	if (!secret) return "";
	return secret.substring(0, 4) + "..." + secret.substring(secret.length - 4);
};

const fetchOneRosterSchools = async () => {
	loading.value = true;
	const res = await FetchService.fetchData("/oneroster/schools", "get");
	oneroster_schools.value = Array.isArray(res) ? res : [];
	loading.value = false;
};

const createSchoolFromOneRoster = async (row: OneRosterSchool) => {
	row.creating = true;
	console.log("Creating school from OneRoster data", row);
	try {
		const res = await FetchService.fetchData(
			`/oneroster/schools/${row.sourced_id}`,
			"post",
			{
				name: row.name,
				oneroster_school_id: row.sourced_id,
			}
		);
		if (res) {
			// Force refresh of district store schools data
			await fetchOneRosterSchools();
			await fetchSchools();
		}
	} finally {
		row.creating = false;
	}
};

const syncData = async () => {
	if (!selectedEntities.value.length) {
		syncStatus.value = {
			message: "Please select at least one entity to sync.",
			color: "warning",
		};
		return;
	}

	syncing.value = true;
	syncStatus.value = null;

	try {
		// Build query string with entity array
		const queryParams = selectedEntities.value
			.map((entity) => `entities[]=${entity}`)
			.join("&");
		const endpoint = `/oneroster/sync?${queryParams}`;
		const response = await FetchService.fetchData(endpoint, "get");

		syncStatus.value = {
			message:
				response.message ||
				"Sync started successfully! This may take a few minutes to complete.",
			color: "success",
		};

		// Start polling for sync status
		pollSyncStatus();
	} catch (error) {
		console.error("Error starting OneRoster sync:", error);
		syncStatus.value = {
			message: "Error starting sync. Please check your configuration and try again.",
			color: "error",
		};
	}
};

const pollSyncStatus = () => {
	// Clear any existing interval first
	if (syncInterval.value) {
		clearInterval(syncInterval.value);
		syncInterval.value = null;
	}

	syncInterval.value = setInterval(async () => {
		try {
			console.log("Polling sync status. Current syncing state:", syncing.value);
			await fetchSyncStatus();

			const completed = isSyncCompleted();
			console.log(
				"Sync completed check:",
				completed,
				"syncLogs length:",
				syncLogs.value.length
			);
			if (syncLogs.value.length > 0) {
				console.log(
					"Sample log entries:",
					syncLogs.value
						.slice(0, 3)
						.map((log) => ({ entity: log.entity, status: log.status }))
				);
			}

			if (completed) {
				console.log("Sync completed, clearing interval");
				clearInterval(syncInterval.value!);
				syncInterval.value = null;
				syncing.value = false;
			}
		} catch (error) {
			console.error("Error polling sync status:", error);
			if (syncInterval.value) {
				clearInterval(syncInterval.value);
				syncInterval.value = null;
			}
			syncing.value = false;
		}
	}, 15000); // Poll every 15 seconds
};

const fetchSyncStatus = async () => {
	try {
		const response = await FetchService.fetchData("/oneroster/status", "get");
		syncLogs.value = Array.isArray(response) ? response : [];

		if (isSyncCompleted()) {
			syncing.value = false;
			if (syncInterval.value) {
				clearInterval(syncInterval.value);
				syncInterval.value = null;
			}
		} else {
			syncing.value = true;
			// Only start polling if not already polling
			if (!syncInterval.value) {
				pollSyncStatus();
			}
		}
	} catch (error) {
		console.error("Error fetching sync logs:", error);
		syncLogs.value = [];
	}
};

const getLogEntryClass = (level: string) => {
	switch (level) {
		case "error":
			return "log-error";
		case "warning":
			return "log-warning";
		case "info":
			return "log-info";
		default:
			return "log-default";
	}
};

const getLogIconColor = (log: any) => {
	const status = log.status?.toLowerCase();
	if (status === SYNC_STATUS_COMPLETED.toLowerCase()) {
		return "success";
	} else if (status === "error" || log.error_message) {
		return "error";
	} else if (status === "running") {
		return "info";
	} else {
		return "primary";
	}
};

const getLogIcon = (log: any) => {
	const status = log.status?.toLowerCase();
	if (status === SYNC_STATUS_COMPLETED.toLowerCase()) {
		return "mdi-check-circle";
	} else if (status === "error" || log.error_message) {
		return "mdi-alert-circle";
	} else if (status === "running") {
		return "mdi-loading mdi-spin";
	} else {
		return "mdi-circle-small";
	}
};

const formatLogTime = (timestamp: string) => {
	return new Date(timestamp).toLocaleString();
};

const getSyncRecordClass = (syncRecord: any) => {
	const status = syncRecord.status?.toLowerCase();
	if (status === SYNC_STATUS_COMPLETED.toLowerCase()) {
		return "sync-completed";
	} else if (status === "error" || syncRecord.error_message) {
		return "sync-error";
	} else if (status === "running") {
		return "sync-running";
	} else {
		return "sync-default";
	}
};

const getEntityClass = (entity: any) => {
	const status = entity.status?.toLowerCase();
	if (status === SYNC_STATUS_COMPLETED.toLowerCase()) {
		return "entity-completed";
	} else if (status === "error" || entity.error_message) {
		return "entity-error";
	} else if (status === "running") {
		return "entity-running";
	} else {
		return "entity-default";
	}
};

const save = async () => {
	saving.value = true;
	try {
		await FetchService.fetchData("/oneroster/schools", "put", {
			schools: oneroster_schools.value,
		});
	} finally {
		saving.value = false;
	}
};

const saveAndClose = async () => {
	await save();
	showSchoolMapping.value = false;
};

const cancelSync = () => {
	// Clear the polling interval
	if (syncInterval.value) {
		clearInterval(syncInterval.value);
		syncInterval.value = null;
	}

	// Reset sync state
	syncing.value = false;
	syncStatus.value = {
		message: "Sync cancelled by user",
		color: "warning",
	};

	console.log("Sync polling cancelled by user");
};

onMounted(async () => {
	if (props.integration_type === "nycdoe") {
		configData.value.version = "nycdoe";
	} else {
		// For non-nycdoe integrations, preserve 1.2 if already set, otherwise default to 1.1
		configData.value.version = configData.value.version === "1.2" ? "1.2" : "1.1";
	}
	await fetchOneRosterConfig();
	await fetchOneRosterSchools();
	await fetchSchools();
	selectedEntities.value = getEntityOptions.value.map((option) => option.value);
	// Fetch initial sync status and logs
	await fetchSyncStatus();
});

// Cleanup intervals on component unmount
onUnmounted(() => {
	if (syncInterval.value) {
		clearInterval(syncInterval.value);
		syncInterval.value = null;
	}
});
</script>

<style scoped>
.oneroster-table-container {
	width: 100%;
}
.oneroster-table {
	table-layout: fixed;
	width: 100%;
}
.table-body-scroll {
	height: 50vh;
	max-height: 50vh;
	overflow-y: auto;
}
.header-cell {
	background: #e5e4db;
	color: #222e3a;
	font-weight: 600;
	font-size: 1rem;
	padding: 3px 3px 3px 3px;
	border-bottom: 2px solid #e3e7ee;
	letter-spacing: 0.04em;
	text-align: left;
	vertical-align: middle;
}
.actions-header {
	text-align: center;
	width: 100px;
}
.reload-btn {
	margin-left: 8px;
	min-width: 32px;
	height: 32px;
	padding: 0;
}
.mapping-btn {
	margin-left: 8px;
}
.logs-btn {
	min-width: 32px;
	height: 32px;
	padding: 0;
}
.sync-progress {
	position: absolute;
	bottom: 0;
	left: 0;
	right: 0;
	border-radius: 0 0 4px 4px;
}
.logs-container {
	max-height: 400px;
	overflow-y: auto;
	border: 1px solid #e0e0e0;
	border-radius: 4px;
}
.log-entry {
	border-bottom: 1px solid #f5f5f5;
}
.log-entry:last-child {
	border-bottom: none;
}
.log-error {
	background-color: #ffebee;
	border-left: 4px solid #f44336;
}
.log-warning {
	background-color: #fff8e1;
	border-left: 4px solid #ff9800;
}
.log-info {
	background-color: #e3f2fd;
	border-left: 4px solid #2196f3;
}
.log-default {
	background-color: #fafafa;
	border-left: 4px solid #9e9e9e;
}
.sync-status-section {
	border-bottom: 1px solid #e0e0e0;
}
.config-display {
	border: 1px solid #e0e0e0;
	border-radius: 4px;
	padding: 12px;
}
.config-display table {
	width: 100%;
}
.config-display td {
	padding: 8px;
}
.config-display td:first-child {
	width: 150px;
}

/* Sync Record Styles */
.sync-record {
	border: 1px solid #e0e0e0;
	border-radius: 8px;
	overflow: hidden;
}
.sync-header {
	border-radius: 8px 8px 0 0;
}
.sync-completed {
	background-color: #e8f5e8;
	border-left: 4px solid #4caf50;
}
.sync-error {
	background-color: #ffebee;
	border-left: 4px solid #f44336;
}
.sync-running {
	background-color: #e3f2fd;
	border-left: 4px solid #2196f3;
}
.sync-default {
	background-color: #fafafa;
	border-left: 4px solid #9e9e9e;
}
.error-message {
	background-color: #ffebee;
	border-left: 3px solid #f44336;
	border-radius: 4px;
}

/* Entity Details Styles */
.entity-details {
	padding: 0 16px 16px 16px;
}
.entity-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
	gap: 12px;
}
.entity-card {
	border: 1px solid #e0e0e0;
	border-radius: 6px;
	background-color: #fafafa;
}
.entity-completed {
	background-color: #e8f5e8;
	border-color: #4caf50;
}
.entity-error {
	background-color: #ffebee;
	border-color: #f44336;
}
.entity-running {
	background-color: #e3f2fd;
	border-color: #2196f3;
}
.entity-default {
	background-color: #fafafa;
	border-color: #e0e0e0;
}
.entity-status {
	font-size: 0.875rem;
	font-weight: 500;
}
.entity-stats {
	color: #666;
}
</style>
